import ScheduleIcon from '@mui/icons-material/Schedule';
import { TextField as MuiTextField } from '@mui/material';

import {
  DesktopDateTimePicker as MuiDesktopDateTimePicker,
  DesktopDateTimePickerProps as MuiDesktopDateTimePickerProps
} from '@mui/x-date-pickers';

import React from 'react';

import { Merge } from 'type-fest';

import { DateFormatEnum } from '_lib/utils/date.utils';

import { TextFieldProps } from '../../TextField/TextField';
import { toAdapterDate, OverrideDatePickerCommonProps } from '../datePickerAdapter';
import {
  PickerProps,
  useBaseAdapterProps,
  useDateBoundProps,
  useMonthAdapterProps,
  usePickerProps,
  useTimeBoundProps
} from '../picker.hooks';

type MuiDesktopDateTimePickerPropsSlotProps = Exclude<MuiDesktopDateTimePickerProps['slotProps'], undefined>;
type DateTimePickerPropsSlotProps = Omit<MuiDesktopDateTimePickerPropsSlotProps, 'field' | 'textField'>;
type DateTimePickerPropsSlots = Omit<Exclude<MuiDesktopDateTimePickerProps['slots'], undefined>, 'field' | 'textField'>;

interface DateTimePickerProps
  extends OverrideDatePickerCommonProps<Omit<MuiDesktopDateTimePickerProps, 'viewRenderers'>> {
  clearable?: PickerProps['clearable'];
  slotProps?: DateTimePickerPropsSlotProps;
  slots?: DateTimePickerPropsSlots;
  textFieldProps?: Merge<
    TextFieldProps,
    {
      /** @default true */
      fullWidth?: TextFieldProps['fullWidth'];
    }
  >;
}

export const DateTimePicker = (props: DateTimePickerProps) => {
  const {
    clearable,
    slots,
    slotProps,
    textFieldProps,
    timezone,
    value,
    onChange,
    onMonthChange,
    minDate,
    maxDate,
    minTime,
    maxTime,
    minDateTime,
    maxDateTime,
    ...restOfProps
  } = props;

  const { openPickerButton: slotPropsOpenPickerButton, ...restOfSlotProps } = slotProps || {};

  const { slotPropsTextFieldOverrides, ...pickerProps } = usePickerProps({ clearable, value, textFieldProps });

  const baseAdapterProps = useBaseAdapterProps({ timezone, value, onChange });
  const monthAdapterProps = useMonthAdapterProps({ onMonthChange });
  const dateBoundProps = useDateBoundProps({ minDate, maxDate, timezone });
  const timeBoundProps = useTimeBoundProps({ minTime, maxTime, timezone });

  return (
    <MuiDesktopDateTimePicker
      format={DateFormatEnum.DateAtTimeShort}
      minDateTime={minDateTime ? toAdapterDate(minDateTime, timezone) : undefined}
      maxDateTime={maxDateTime ? toAdapterDate(maxDateTime, timezone) : undefined}
      {...baseAdapterProps}
      {...monthAdapterProps}
      {...dateBoundProps}
      {...timeBoundProps}
      {...restOfProps}
      slotProps={{
        ...restOfSlotProps,
        field: pickerProps.slotPropsField,
        openPickerButton: { ...slotPropsOpenPickerButton, ref: pickerProps.slotPropsOpenPickerButtonRef },
        textField: slotPropsTextFieldOverrides
      }}
      closeOnSelect={true}
      // TODO: see https://mui.com/x/react-date-pickers/custom-field/#using-material-textfield
      enableAccessibleFieldDOMStructure={false}
      slots={{
        openPickerIcon: ScheduleIcon,
        ...slots,
        textField: MuiTextField
      }}
      // Using 288 as a default to support a full 24 hours with a minute step of 5 (which is the default)
      thresholdToRenderTimeInASingleColumn={288}
    />
  );
};
